import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
answer: string | React.ReactElement;
  category: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData: FAQItem[] = [
    // ========================================
    // CONFIGURACIÓN INICIAL
    // ========================================
    {
      category: "Configuración Inicial",
      question: "¿Cómo completo mi perfil profesional?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Dashboard:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Hacé click en <strong>"Editar Perfil"</strong></li>
            <li>Verás 3 pestañas:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>Información Básica:</strong> Tu nombre, apellido, especialidad, años de experiencia</li>
                <li><strong>Información del Negocio:</strong> Nombre de tu consultorio, dirección, descripción de servicios</li>
                <li><strong>Fotos:</strong> Foto de perfil y foto de portada</li>
              </ul>
            </li>
            <li>Completá todos los campos que puedas</li>
            <li><strong>⚠️ IMPORTANTE:</strong> Hacé click en <strong>"Guardar"</strong> antes de salir</li>
          </ol>
        </div>
      )
    },
    {
      category: "Configuración Inicial",
      question: "¿Cómo subo mi foto de perfil y portada?",
      answer: (
        <div className="space-y-3">
          <p><strong>Pasos:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir a <strong>Editar Perfil → Pestaña "Fotos"</strong></li>
            <li>Click en la imagen que querés cambiar (perfil o portada)</li>
            <li>Aparecerá un botón para <strong>"Subir foto"</strong></li>
            <li>Seleccioná la imagen desde tu computadora o celular</li>
            <li>La imagen se subirá automáticamente</li>
            <li><strong>⚠️ IMPORTANTE:</strong> Hacé click en <strong>"Guardar"</strong> antes de salir</li>
          </ol>
          <p className="text-sm text-gray-600 mt-2">💡 <strong>Consejo:</strong> Usá fotos profesionales de buena calidad. La foto de perfil aparecerá en los resultados de búsqueda.</p>
        </div>
      )
    },

    // ========================================
    // SERVICIOS
    // ========================================
    {
      category: "Servicios",
      question: "¿Cómo agregar mis servicios?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel Principal:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Click en <strong>"Servicios"</strong> (ícono naranja con engranaje)</li>
            <li>Click en <strong>"Agregar Nuevo Servicio"</strong></li>
            <li>Completá los datos:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>Nombre del servicio:</strong> Ejemplo: "Consulta", "Control", "Tratamiento"</li>
                <li><strong>Categoría:</strong> Seleccioná de la lista</li>
                <li><strong>Duración:</strong> Tiempo en minutos que dura la consulta</li>
                <li><strong>Precio:</strong> Valor de la consulta</li>
              </ul>
            </li>
            <li>Click en <strong>"Crear Servicio"</strong></li>
            <li>El servicio aparecerá en tu perfil público y los pacientes podrán seleccionarlo al reservar</li>
          </ol>
        </div>
      )
    },
    {
      category: "Servicios",
      question: "¿Cómo editar o eliminar un servicio?",
      answer: (
        <div className="space-y-3">
          <p><strong>Para editar:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir a <strong>"Servicios"</strong></li>
            <li>Buscar el servicio que querés modificar</li>
            <li>Click en <strong>"Editar"</strong></li>
            <li>Modificar los campos necesarios</li>
            <li>Click en <strong>"Guardar cambios"</strong></li>
          </ol>
          <p className="mt-3"><strong>Para eliminar:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir a <strong>"Servicios"</strong></li>
            <li>Buscar el servicio que querés eliminar</li>
            <li>Click en <strong>"Eliminar"</strong> o ícono de basura</li>
            <li>Confirmar la eliminación</li>
          </ol>
          <p className="text-sm text-gray-600 mt-2">⚠️ <strong>Nota:</strong> Si eliminás un servicio, los turnos ya agendados con ese servicio NO se cancelan.</p>
        </div>
      )
    },

    // ========================================
    // HORARIOS
    // ========================================
    {
      category: "Horarios",
      question: "¿Cómo configurar mis horarios de atención?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel Principal:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Click en <strong>"Horarios"</strong> (ícono celeste con reloj)</li>
            <li><strong>Marcar días laborables:</strong>
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>✅ Tildar los días que SÍ trabajás</li>
                <li>⬜ Destildar los días que NO trabajás</li>
              </ul>
            </li>
            <li><strong>Configurar horario de trabajo:</strong>
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>Hora de inicio:</strong> Seleccionar cuándo empezás a atender</li>
                <li><strong>Hora de fin:</strong> Seleccionar cuándo terminás de atender</li>
              </ul>
            </li>
            <li><strong>Descanso (opcional):</strong>
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Si tenés horario de almuerzo o descanso, configuralo acá</li>
                <li>Si trabajás de corrido, destildá esta opción</li>
              </ul>
            </li>
            <li><strong>⚠️ IMPORTANTE:</strong> Click en <strong>"Guardar"</strong> antes de salir</li>
          </ol>
        </div>
      )
    },
    {
      category: "Horarios",
      question: "¿Cómo configurar la anticipación para reservar turnos?",
      answer: (
        <div className="space-y-3">
          <p>En la sección <strong>"Horarios"</strong> también podés configurar:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Anticipación para agendar:</strong> Cuánto tiempo con anticipación pueden reservar turnos tus pacientes
              <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                <li>Por defecto: 1 mes</li>
                <li>Podés configurarlo a tu gusto (1 semana, 15 días, 2 meses, etc.)</li>
              </ul>
            </li>
            <li><strong>Tiempo entre citas:</strong> Minutos de descanso entre cada turno
              <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                <li>Te da tiempo para ordenar el consultorio</li>
                <li>Preparar el próximo paciente</li>
                <li>Ejemplo: 10-15 minutos entre turnos</li>
              </ul>
            </li>
          </ul>
        </div>
      )
    },

    // ========================================
    // MÉTODOS DE PAGO
    // ========================================
    {
      category: "Métodos de Pago",
      question: "¿Qué opciones de pago puedo configurar?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel → "Pagos":</strong></p>
          <p><strong>Primero elegí QUÉ querés cobrar:</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>💰 <strong>Consulta completa:</strong> El paciente paga el precio total al reservar</li>
            <li>🎫 <strong>Solo seña:</strong> El paciente paga un adelanto (el resto lo pagá en el consultorio)</li>
            <li>💵 <strong>Solo efectivo:</strong> Sin pago online, todo se paga en el consultorio</li>
          </ul>
          <p className="text-sm bg-blue-50 p-3 rounded-lg mt-2">💡 <strong>Recomendado:</strong> Usar "Solo seña" ayuda a reducir las inasistencias sin cobrar todo por adelantado.</p>
        </div>
      )
    },
    {
      category: "Métodos de Pago",
      question: "¿Cómo funciona el pago por transferencia bancaria?",
      answer: (
        <div className="space-y-3">
          <p><strong>Configuración:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir a <strong>"Pagos" → "Transferencia Bancaria"</strong></li>
            <li>Completar tus datos:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>CBU o Alias</strong> de tu cuenta</li>
                <li><strong>Nombre completo</strong> (tal como aparece en tu cuenta bancaria)</li>
              </ul>
            </li>
            <li>Guardar configuración</li>
          </ol>
          <p className="mt-3"><strong>¿Cómo funciona para el paciente?</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>El paciente reserva el turno</li>
            <li>Ve tus datos bancarios en pantalla</li>
            <li>Hace la transferencia desde su banco</li>
            <li>Sube el comprobante a la plataforma</li>
            <li><strong>VOS</strong> tenés que verificar el comprobante en <strong>"Verificar Transferencias"</strong></li>
            <li>Una vez aprobado, el turno queda confirmado</li>
          </ol>
        </div>
      )
    },
    {
      category: "Métodos de Pago",
      question: "¿Cómo vincular Mercado Pago? ¿Cuánto cobra de comisión?",
      answer: (
        <div className="space-y-3">
          <p><strong>Ventajas de Mercado Pago:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>✅ Los pacientes pueden pagar con tarjeta de crédito/débito</li>
            <li>✅ El pago se acredita automáticamente (sin verificación manual)</li>
            <li>✅ Es seguro y rápido (el paciente paga en 2 clics)</li>
          </ul>
          
          <p className="mt-3"><strong>Comisiones:</strong></p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
            <p className="text-sm">⚠️ Mercado Pago cobra aproximadamente <strong>6% de comisión</strong></p>
            <p className="text-sm mt-1">Ejemplo: Si cobrás $1000, recibirás $940 en tu cuenta</p>
            <p className="text-sm mt-1">El dinero se acredita en <strong>10-14 días</strong></p>
            <p className="text-sm mt-1 font-semibold">Nota: Estos porcentajes y tiempos dependen de Mercado Pago, NO de ConsultAR</p>
          </div>

          <p className="mt-3"><strong>¿Cómo vincular?</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir a <strong>"Pagos" → "Mercado Pago"</strong></li>
            <li>Click en <strong>"Vincular Mercado Pago"</strong></li>
            <li>Te redirige a Mercado Pago</li>
            <li>Iniciá sesión con tu usuario y contraseña de Mercado Pago</li>
            <li>Otorgá permisos a ConsultAR para realizar cobros</li>
            <li>Si todo salió bien, verás <strong>"Vinculación exitosa"</strong></li>
            <li>Tildá la opción <strong>"Cobrar por Mercado Pago"</strong></li>
            <li>Click en <strong>"Guardar Configuración"</strong></li>
          </ol>
          <p className="text-sm text-red-600 mt-2">⚠️ ¿Problema en la vinculación? Contactá a soporte técnico.</p>
        </div>
      )
    },
    {
      category: "Métodos de Pago",
      question: "¿Dónde verifico los pagos por transferencia?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel → "Verificar Transferencias":</strong></p>
          <p>Cuando un paciente paga por transferencia:</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>El paciente hace la transferencia a tu cuenta</li>
            <li>El paciente sube el comprobante a la plataforma</li>
            <li>El comprobante aparece en tu pestaña <strong>"Pendientes"</strong></li>
            <li>VOS revisás el comprobante</li>
            <li>Tenés dos opciones:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>✅ Aprobar:</strong> Si el pago está correcto y acreditado
                  <ul className="list-disc list-inside ml-8 text-sm">
                    <li>El turno se confirma automáticamente</li>
                    <li>El paciente recibe un email de confirmación</li>
                  </ul>
                </li>
                <li><strong>❌ Rechazar:</strong> Si el comprobante es incorrecto o el pago no se acreditó
                  <ul className="list-disc list-inside ml-8 text-sm">
                    <li>El turno se cancela automáticamente</li>
                    <li>El paciente recibe un email de rechazo</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ol>
          <p className="text-sm bg-blue-50 p-3 rounded-lg mt-2">💡 <strong>Importante:</strong> Revisá los comprobantes regularmente para no demorar la confirmación de turnos.</p>
        </div>
      )
    },

    // ========================================
    // GESTIÓN DE TURNOS
    // ========================================
    {
      category: "Gestión de Turnos",
      question: "¿Cómo veo mis turnos del día?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel → "Calendario":</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Verás todos tus turnos organizados por fecha</li>
            <li>Podés filtrar por:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>Todos los turnos</li>
                <li>Confirmados</li>
                <li>Pendientes</li>
                <li>Completados</li>
              </ul>
            </li>
            <li>Click en un turno para ver los detalles completos</li>
          </ul>
        </div>
      )
    },
    {
      category: "Gestión de Turnos",
      question: "¿Cómo confirmar, reprogramar o cancelar un turno?",
      answer: (
        <div className="space-y-3">
          <p><strong>Para CONFIRMAR un turno -Profesional-:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir al <strong>"Calendario"</strong></li>
            <li>Click en el turno</li>
            <li>Click en <strong>"Confirmar turno"</strong></li>
            <li>El paciente recibirá un email de confirmación</li>
          </ol>

          <p className="mt-3"><strong>Para REPROGRAMAR un turno -Paciente disponible solo 48hs antes-:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir al <strong>"Calendario"</strong></li>
            <li>Click en el turno</li>
            <li>Click en <strong>"Reprogramar"</strong></li>
            <li>Seleccioná la nueva fecha y hora</li>
            <li>Confirmar cambios</li>
            <li>El paciente recibirá un email con la nueva fecha</li>
          </ol>

          <p className="mt-3"><strong>Para CANCELAR un turno:</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Ir al <strong>"Calendario"</strong></li>
            <li>Click en el turno</li>
            <li>Click en <strong>"Cancelar turno"</strong></li>
            <li>Opcionalmente, podés agregar un motivo</li>
            <li>Confirmar cancelación</li>
            <li>El paciente recibirá un email informándole la cancelación</li>
          </ol>
        </div>
      )
    },

    // ========================================
    // PORTFOLIO Y PERFIL PÚBLICO
    // ========================================
    {
      category: "Portfolio y Perfil Público",
      question: "¿Cómo subo fotos a mi portfolio?",
      answer: (
        <div className="space-y-3">
          <p><strong>Desde el Panel → "Portfolio Público":</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Click en <strong>"Agregar foto"</strong> o <strong>"+"</strong></li>
            <li>Seleccioná la imagen desde tu dispositivo</li>
            <li>Opcionalmente, agregá una descripción a la foto</li>
            <li>Click en <strong>"Subir"</strong></li>
            <li>La foto aparecerá en tu perfil público</li>
          </ol>
          <p className="text-sm bg-blue-50 p-3 rounded-lg mt-2">💡 <strong>Consejo:</strong> Usá el portfolio para mostrar tu consultorio, equipo, o resultados de tratamientos (siempre respetando la privacidad de los pacientes).</p>
        </div>
      )
    },
    {
      category: "Portfolio y Perfil Público",
      question: "¿Cómo comparto mi link de reservas?",
      answer: (
        <div className="space-y-3">
          <p><strong>En tu Dashboard verás una sección llamada "Tu Link para que tus clientes reserven"</strong></p>
          <p>Tu link personal tiene este formato:</p>
          <div className="bg-gray-100 p-2 rounded text-sm font-mono">
            consultarapp.com/p/tu-nombre
          </div>
          
          <p className="mt-3"><strong>¿Cómo compartirlo?</strong></p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Click en <strong>"Copiar Link"</strong></li>
            <li>El link se copia automáticamente</li>
            <li>Pegalo donde quieras:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li>📱 Bio de Instagram o Facebook</li>
                <li>💬 Mensajes de WhatsApp a tus pacientes</li>
                <li>📧 Firma de email</li>
                <li>🔗 Tarjeta de presentación digital</li>
              </ul>
            </li>
          </ol>
          <p className="mt-3">También podés hacer click en <strong>"Ver perfil"</strong> para ver cómo lo ven tus pacientes.</p>
        </div>
      )
    },

    // ========================================
    // SUSCRIPCIÓN Y PERIODO DE PRUEBA
    // ========================================
    {
      category: "Suscripción",
      question: "¿Cómo funciona el período de prueba?",
      answer: (
        <div className="space-y-3">
          <p><strong>Al registrarte como profesional, tenés:</strong></p>
          <div className="bg-green-50 border-l-4 border-green-400 p-3">
            <p className="font-semibold">🎉 15 días de prueba GRATIS</p>
            <p className="text-sm mt-1">Acceso completo a todas las funcionalidades sin costo</p>
          </div>
          
          <p className="mt-3"><strong>Durante el período de prueba podés:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>✅ Configurar tu perfil completo</li>
            <li>✅ Agregar servicios y horarios</li>
            <li>✅ Recibir reservas de pacientes</li>
            <li>✅ Cobrar por transferencia o Mercado Pago</li>
            <li>✅ Usar todas las funcionalidades sin límites</li>
          </ul>

          <p className="mt-3"><strong>Después del período de prueba:</strong></p>
          <p>Tendrás que suscribirte para seguir recibiendo reservas. El costo de la suscripción es accesible y podés pagarla mensualmente.</p>
        </div>
      )
    },
    {
      category: "Suscripción",
      question: "¿Qué pasa si no me suscribo después del período de prueba?",
      answer: (
        <div className="space-y-3">
          <p><strong>Si no te suscribís después de los 15 días:</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>❌ Tu perfil público dejará de estar visible</li>
            <li>❌ Los pacientes no podrán reservar turnos nuevos</li>
            <li>✅ Mantenés acceso a tu cuenta y datos</li>
            <li>✅ Podés ver tus turnos pasados</li>
            <li>✅ Los turnos ya agendados NO se cancelan</li>
          </ul>
          <p className="mt-3">En cualquier momento podés suscribirte y tu perfil volverá a estar activo inmediatamente.</p>
        </div>
      )
    },

    // ========================================
    // SOPORTE
    // ========================================
    {
      category: "Soporte",
      question: "¿Cómo contacto al soporte técnico?",
      answer: (
        <div className="space-y-3">
          <p><strong>Si tenés algún problema o duda, podés contactarnos por:</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>💬 <strong>WhatsApp:</strong> Click en el botón verde flotante en la esquina inferior derecha</li>
            <li>📧 <strong>Email:</strong> noreply.consultar@gmail.com</li>
          </ul>
          <p className="mt-3"><strong>Te respondemos en:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Horario de atención: Lunes a Viernes de 9 a 18hs</li>
            <li>Tiempo de respuesta: Menos de 24 horas hábiles</li>
          </ul>
          <p className="text-sm bg-blue-50 p-3 rounded-lg mt-2">💡 <strong>Consejo:</strong> Antes de contactarnos, revisá este FAQ - probablemente tu duda ya esté respondida acá.</p>
        </div>
      )
    }
  ];

  // Agrupar por categoría
  const categories = Array.from(new Set(faqData.map(item => item.category)));

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Preguntas Frecuentes
        </h1>
        <p className="text-lg text-gray-600">
          Todo lo que necesitás saber para usar ConsultAR
        </p>
      </div>

      {categories.map((category, catIndex) => (
        <div key={catIndex} className="mb-8">
          <h2 className="text-2xl font-bold text-blue-600 mb-4 pb-2 border-b-2 border-blue-200">
            {category}
          </h2>
          
          <div className="space-y-3">
            {faqData
              .filter(item => item.category === category)
              .map((item, index) => {
                const globalIndex = faqData.indexOf(item);
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => toggleQuestion(globalIndex)}
                      className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 pr-4">
                        {item.question}
                      </span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    
                    {isOpen && (
                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <div className="text-gray-700 leading-relaxed">
                          {item.answer}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {/* CTA final */}
      <div className="mt-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-xl p-8 text-center text-white">
        <h3 className="text-2xl font-bold mb-2">¿No encontraste tu respuesta?</h3>
        <p className="mb-4">Estamos acá para ayudarte</p>
        <a
          href="https://wa.me/5493794080538?text=Hola,%20necesito%20ayuda%20con%20ConsultAR"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          Contactar Soporte
        </a>
      </div>
    </div>
  );
};

export default FAQ;